import { NgModule, ModuleWithProviders } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { FieldType, FormlyModule, FORMLY_CONFIG } from '@ngx-formly/core';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';
import { FormlySelectModule } from '@ngx-formly/core/select';
import { ColorPickerModule } from 'ngx-color-picker';

import { FormFieldWrapperComponent } from './wrappers/form-field.component';
import { NoLabelWrapperComponent } from './wrappers/no-label.component';
import { NoMarginFormFieldWrapperComponent } from './wrappers/no-margin-form-field.component';
import { FieldSetWrapperComponent } from './wrappers/field-set.component';
import { TableWrapperComponent } from './wrappers/table.component';
import { TableRowWrapperComponent } from './wrappers/table-row.component';
import { TableColumnWrapperComponent } from './wrappers/table-column.component';
import { FormlyWrapperModernSwitch } from './wrappers/modern-switch-wrapper.component';

import { FieldType as CustomFieldType } from './enums/field-type';
import { FieldWrapper } from './enums/field-wrapper';

import { FormlyFieldInput } from './types/input.component';
import { FormlyFieldCheckbox } from './types/checkbox.component';
import { FormlyFieldNumericInput } from './types/numeric-input.component';
import { FormlyFieldSelect } from './types/select.component';
import { FormlyFieldTextArea } from './types/textarea.component';
import { FormlyFieldColorPicker } from './types/color-picker.component';
import { FormlyFieldDatepicker } from './types/datepicker.component';
import { FormlyFieldDateTimepicker } from './types/datetimepicker.component';
import { FormlyFieldCombobox } from './types/combobox.component';
import { FormlyFieldRadio } from './types/radio.component';
import { FormlyFieldSeparator } from './types/separator.component';
import { FormlyFieldHtml } from './types/html.component';
import { FormlyFieldMultiCheckbox } from './types/multi-checkbox.component';
import { FormlyFieldInputGroup } from './types/input-group.component';
import { FormlyFieldCustomCombobox } from './types/custom-combobox.component';
import { FormlyFieldTimepicker } from './types/timepicker.component';
import { FormlyFieldSegmented } from './types/segmented.component';
import { ButtonsModule as KendoButtonsModule } from '@progress/kendo-angular-buttons';
import { FormlyFieldButton } from './types/button.component';
import { FormlyFieldTemplate } from './types/template.component';
import { FormlyFileSelect } from './types/file-select.component';
import { FormlyFieldRepeatSection } from './types/repeat-section.component';
import { FormlySectionHeader } from './types/section-header.component';
import { FormlyFieldPhoneInput } from './types/phone-input.component';
import { FormlyFieldImageUpload } from './types/image-upload.component';
import { FormlyFieldPasswordIndicator } from './types/password-with-indicator.component';

import { CardWrapperComponent } from './wrappers/card-wrapper.component';
import { NoLabelNoMarginWrapperComponent } from './wrappers/no-label-no-margin.component';
import { HorizontalFormFieldWrapperComponent } from './wrappers/horizontal-form-field.component';
import { InputGroupWrapperComponent } from './wrappers/input-group.component';
import { SectionHeaderComponent } from '../components/section-header/section-header.component';

export function showError(field: FieldType) {
  return (
    field.formControl?.invalid &&
    (field.formControl?.touched ||
      field.formControl?.dirty ||
      (field.options.parentForm?.submitted &&
        field.options.parentForm?.invalid) ||
      !!field.field.validation?.show)
  );
}

export function emailValidator(control: AbstractControl): ValidationErrors {
  if (control?.value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(control.value)
      ? null
      : { email: true };
  }
  return null;
}

export function zipCodeValidator(control: AbstractControl): ValidationErrors {
  const zipCode = control?.value?.trim().replace(/-$/, '').trim();
  if (zipCode) {
    return /^[0-9]{5}(?:-[0-9]{4})?$/.test(zipCode) ? null : { zipcode: true };
  }
  return null;
}

export function urlValidator(control: AbstractControl): ValidationErrors {
  let testUrl = control?.value?.trim();
  if (!testUrl) {
    return null;
  }

  // Add protocol if missing
  if (!testUrl.match(/^https?:\/\//i)) {
    testUrl = 'https://' + testUrl;
  }

  try {
    const urlObj = new URL(testUrl);

    // Validate protocol
    if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
      return { url: true };
    }

    // Validate hostname exists and has at least one dot
    if (!urlObj.hostname || !urlObj.hostname.includes('.')) {
      return { url: true };
    }

    // RFC 1034/1035 compliant hostname validation
    // Each label: alphanumeric start/end, can contain hyphens (max 63 chars per label)
    // Must end with a valid TLD (2+ letter characters)
    if (!/^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)*[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.([a-z]{2,})$/i.test(urlObj.hostname)) {
      return { url: true };
    }

    return null;
  } catch {
    // Invalid URL format
    return { url: true };
  }
}

export function phoneNumberValidator(
  control: AbstractControl
): ValidationErrors {
  let phone = control?.value?.trim().replace(/\D/g, '');
  if (phone) {
    if (phone.length === 10) {
      // US/Canada without country code: (123) 456-7890 or 123-456-7890
      return /^[2-9]\d{2}[2-9]\d{2}\d{4}$/.test(phone) ? null : { phone: true };
    } else if (phone.length === 11 && phone.startsWith('1')) {
      // US/Canada with country code: +1 (123) 456-7890
      return /^1[2-9]\d{2}[2-9]\d{2}\d{4}$/.test(phone) ? null : { phone: true };
    } else if (phone.length >= 7 && phone.length <= 15) {
      // International format: at least 7 digits, max 15
      return /^\d{7,15}$/.test(phone) ? null : { phone: true };
    }

    return { phone: true };
  }

  return null;
}

export function internationalPhoneValidatorFactory(field: any) {
  return (control: AbstractControl): ValidationErrors => {
    const value = control?.value?.trim();
    if (!value) {
      return null; // Empty values handled by required validator
    }

    // Get ITI instance from the phone input component
    const componentInstance = (field as any)?._componentRefs?.[0]?.instance;
    const iti = componentInstance?.getItiInstance?.();

    if (iti) {
      // Use intl-tel-input's comprehensive validation
      return iti.isValidNumber() ? null : { internationalPhone: true };
    }

    // Fallback to basic E.164 format check if ITI not available
    return /^\+\d{7,15}$/.test(value) ? null : { internationalPhone: true };
  };
}

export function internationalPhoneValidationMessage(err: any, field: any) {
  return `${field.formControl.value} is not a valid international phone number`;
}

export function requiredValidationMessage(err, field) {
  return `${field.props.label} is required`;
}

export function emailValidationMessage(err, field) {
  return `${field.formControl.value} is not a valid email address`;
}

export function urlValidationMessage(err, field) {
  return `${field.formControl.value} is not a valid url`;
}

export function phoneNumberValidationMessage(err, field) {
  return `${field.formControl.value} is not a valid phone number`;
}

export function zipCodeValidationMessage(err, field) {
  return `The ${field.props.label} format is invalid`;
}

export function patternValidationMessage(err, field) {
  return `${field.props.label} is not in the valid format`;
}

export function maskValidationMessage(err, field) {
  return `${field.props.label} is not in the valid format`;
}

export function minValidationMessage(err, field) {
  return `${field.props.label} should be greater than ${field.props.min}`;
}

export function minErrorValidationMessage(err, field) {
  return `${field.props.label} should be greater`;
}

export function maxValidationMessage(err, field) {
  return `${field.props.label} should be less than ${field.props.max}`;
}

export function maxErrorValidationMessage(err, field) {
  return `${field.props.label} should be lower`;
}

export function minLengthValidationMessage(err, field) {
  return `${field.props.label} should be at least ${field.props.minLength} characters`;
}

export function maxLengthValidationMessage(err, field) {
  return `${field.props.label} should be less than ${field.props.maxLength} characters`;
}

export function timeRangeValidationMessage(err, field) {
  const minTime = (
    field.props?.minDate ?? new Date(0, 0, 0, 0, 0, 0)
  ).toLocaleTimeString([], {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });

  const maxTime = (
    field.props?.maxDate ?? new Date(0, 0, 0, 0, 0, 0)
  ).toLocaleTimeString([], {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });

  return `Only values between ${minTime} and ${maxTime} are valid!`;
}

export function maxDateValidationMessage(err, field) {
  return `${field.props.label} should be less than ${field.props.max}`;
}

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ColorPickerModule,
    // The segmented field type is a Kendo ButtonGroup. SharedModule already imports and
    // exports this, but DynamicFormsModule declares its own field components, so it needs
    // the module in its OWN imports for the template to compile. (CHAT-995)
    KendoButtonsModule,
    SectionHeaderComponent,
    FormlyFieldImageUpload,
    FormlyModule,
    FormlySelectModule,
    FormlyBootstrapModule,
  ],
  exports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FormlyModule,
    FormlySelectModule,
    FormlyBootstrapModule,
  ],
  declarations: [
    FormFieldWrapperComponent,
    HorizontalFormFieldWrapperComponent,
    NoLabelWrapperComponent,
    NoLabelNoMarginWrapperComponent,
    NoMarginFormFieldWrapperComponent,
    FieldSetWrapperComponent,
    TableWrapperComponent,
    TableRowWrapperComponent,
    TableColumnWrapperComponent,
    CardWrapperComponent,
    InputGroupWrapperComponent,
    FormlyWrapperModernSwitch,
    FormlyFieldInput,
    FormlyFieldTextArea,
    FormlyFieldCheckbox,
    FormlyFieldMultiCheckbox,
    FormlyFieldNumericInput,
    FormlyFieldSelect,
    FormlyFieldColorPicker,
    FormlyFieldDatepicker,
    FormlyFieldTimepicker,
    FormlyFieldDateTimepicker,
    FormlyFieldCombobox,
    FormlyFieldCustomCombobox,
    FormlyFieldRadio,
    FormlyFieldSeparator,
    FormlyFieldHtml,
    FormlyFieldInputGroup,
    FormlyFieldSegmented,
    FormlyFieldButton,
    FormlyFileSelect,
    FormlyFieldRepeatSection,
    FormlySectionHeader,
    FormlyFieldPhoneInput,
    FormlyFieldPasswordIndicator,
  ],
  providers: [],
})
export class DynamicFormsModule {
  static forRoot(): ModuleWithProviders<DynamicFormsModule> {
    return {
      ngModule: DynamicFormsModule,
      providers: [
        {
          provide: FORMLY_CONFIG,
          multi: true,
          useValue: {
            wrappers: [
              {
                name: FieldWrapper.FormField,
                component: FormFieldWrapperComponent,
              },
              {
                name: FieldWrapper.HorizontalFormField,
                component: HorizontalFormFieldWrapperComponent,
              },
              {
                name: FieldWrapper.NoLabel,
                component: NoLabelWrapperComponent,
              },
              {
                name: FieldWrapper.NoLabelNoMargin,
                component: NoLabelNoMarginWrapperComponent,
              },
              {
                name: 'modern-switch-wrapper',
                component: FormlyWrapperModernSwitch,
              },
              {
                name: FieldWrapper.NoMarginFormField,
                component: NoMarginFormFieldWrapperComponent,
              },
              {
                name: FieldWrapper.FieldSet,
                component: FieldSetWrapperComponent,
              },
              {
                name: FieldWrapper.Table,
                component: TableWrapperComponent,
              },
              {
                name: FieldWrapper.TableRow,
                component: TableRowWrapperComponent,
              },
              {
                name: FieldWrapper.TableColumn,
                component: TableColumnWrapperComponent,
              },
              {
                name: FieldWrapper.Card,
                component: CardWrapperComponent,
              },
              {
                name: FieldWrapper.InputGroup,
                component: InputGroupWrapperComponent,
              },
            ],
            validators: [
              {
                name: 'email',
                validation: emailValidator,
              },
              {
                name: 'zipcode',
                validation: zipCodeValidator,
              },
              {
                name: 'url',
                validation: urlValidator,
              },
              {
                name: 'phone',
                validation: phoneNumberValidator,
              },
              {
                name: 'internationalPhone',
                validation: internationalPhoneValidatorFactory,
              },
            ],
            validationMessages: [
              {
                name: 'email',
                message: emailValidationMessage,
              },
              {
                name: 'url',
                message: urlValidationMessage,
              },
              {
                name: 'phone',
                message: phoneNumberValidationMessage,
              },
              {
                name: 'internationalPhone',
                message: internationalPhoneValidationMessage,
              },
              {
                name: 'required',
                message: requiredValidationMessage,
              },
              {
                name: 'patternError',
                message: patternValidationMessage,
              },
              {
                name: 'zipcode',
                message: zipCodeValidationMessage,
              },
              {
                name: 'mask',
                message: maskValidationMessage,
              },
              {
                name: 'min',
                message: minValidationMessage,
              },
              {
                name: 'minError',
                message: minErrorValidationMessage,
              },
              {
                name: 'max',
                message: maxValidationMessage,
              },
              {
                name: 'maxError',
                message: maxErrorValidationMessage,
              },
              {
                name: 'minLength',
                message: minLengthValidationMessage,
              },
              {
                name: 'maxLength',
                message: maxLengthValidationMessage,
              },
              {
                name: 'timeRangeError',
                message: timeRangeValidationMessage,
              },
            ],
            types: [
              {
                name: CustomFieldType.Input,
                component: FormlyFieldInput,
                wrappers: [FieldWrapper.FormField],
              },
              {
                name: CustomFieldType.Textarea,
                component: FormlyFieldTextArea,
                wrappers: [FieldWrapper.FormField],
              },
              {
                name: CustomFieldType.Checkbox,
                component: FormlyFieldCheckbox,
                wrappers: [FieldWrapper.NoLabel],
              },
              {
                name: CustomFieldType.MultiCheckbox,
                component: FormlyFieldMultiCheckbox,
                wrappers: [FieldWrapper.NoLabel],
              },
              {
                name: CustomFieldType.Select,
                component: FormlyFieldSelect,
                wrappers: [FieldWrapper.FormField],
              },
              {
                name: CustomFieldType.Numeric,
                component: FormlyFieldNumericInput,
                wrappers: [FieldWrapper.FormField],
              },
              {
                name: CustomFieldType.ColorPicker,
                component: FormlyFieldColorPicker,
                wrappers: [FieldWrapper.FormField],
              },
              {
                name: CustomFieldType.Segmented,
                component: FormlyFieldSegmented,
                wrappers: [FieldWrapper.FormField],
              },
              {
                name: CustomFieldType.DatePicker,
                component: FormlyFieldDatepicker,
                wrappers: [FieldWrapper.FormField],
              },
              {
                name: CustomFieldType.TimePicker,
                component: FormlyFieldTimepicker,
                wrappers: [FieldWrapper.FormField],
              },
              {
                name: CustomFieldType.DateTimePicker,
                component: FormlyFieldDateTimepicker,
                wrappers: [FieldWrapper.FormField],
              },
              {
                name: CustomFieldType.Combobox,
                component: FormlyFieldCombobox,
                wrappers: [FieldWrapper.FormField],
              },
              {
                name: CustomFieldType.CustomCombobox,
                component: FormlyFieldCustomCombobox,
                wrappers: [FieldWrapper.FormField],
              },
              {
                name: CustomFieldType.Radio,
                component: FormlyFieldRadio,
                wrappers: [FieldWrapper.FormField],
              },
              {
                name: CustomFieldType.Separator,
                component: FormlyFieldSeparator,
                wrappers: [FieldWrapper.NoLabel],
              },
              {
                name: CustomFieldType.Html,
                component: FormlyFieldHtml,
                wrappers: [FieldWrapper.FormField],
              },
              {
                name: CustomFieldType.InputGroup,
                component: FormlyFieldInputGroup,
                wrappers: [FieldWrapper.FormField],
              },
              {
                name: CustomFieldType.Button,
                component: FormlyFieldButton,
                wrappers: [FieldWrapper.NoLabel],
              },
              {
                name: CustomFieldType.Template,
                component: FormlyFieldTemplate,
                wrappers: [FieldWrapper.NoLabel],
              },
              {
                name: CustomFieldType.FileSelect,
                component: FormlyFileSelect,
                wrappers: [FieldWrapper.NoLabel],
              },
              {
                name: CustomFieldType.RepeatSection,
                component: FormlyFieldRepeatSection,
                wrappers: [FieldWrapper.NoLabel],
              },
              {
                name: CustomFieldType.SectionHeader,
                component: FormlySectionHeader,
                wrappers: [FieldWrapper.NoLabel],
              },
              {
                name: CustomFieldType.PhoneInput,
                component: FormlyFieldPhoneInput,
                wrappers: [FieldWrapper.FormField],
              },
              {
                name: CustomFieldType.ImageUpload,
                component: FormlyFieldImageUpload,
                wrappers: [FieldWrapper.NoLabel],
              },
              {
                name: CustomFieldType.PasswordIndicator,
                component: FormlyFieldPasswordIndicator,
                wrappers: [FieldWrapper.FormField],
              },
            ],
            extras: {
              showError: showError,
              lazyRender: true,
              resetFieldOnHide: true,
              renderFormlyFieldElement: true,
            },
          },
        },
      ],
    };
  }
}
